import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Tag, Space, Modal, Descriptions, message, Popconfirm, Input, Tooltip, Empty } from 'antd';
import { ReloadOutlined, EyeOutlined } from '@ant-design/icons';
import sslService from '../../services/sslService';

// ACME 订阅（Sectigo CaaS，GoGetSSL API v2）：列表 / 详情 / 从 CA 刷新 / 取消（CA 取消 + 订阅结束；退款走账单退款）
const STATUS = { active: ['正常', 'green'], pending: ['开通中', 'processing'], creating: ['开通中', 'processing'],
  awaiting: ['待加域名', 'blue'], unknown: ['结果待核实', 'orange'], cancelled: ['已取消', 'default'] };

export default function AcmeSubscriptions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState({ current: 1, pageSize: 20, total: 0 });
  const [detail, setDetail] = useState(null);
  const [itemId, setItemId] = useState('');

  const load = useCallback(async (p = 1, size = 20) => {
    setLoading(true);
    try {
      const r = await sslService.getAcmeList({ page: p, per_page: size });
      setRows(r.data?.items || []);
      setPage({ current: p, pageSize: size, total: r.data?.total || 0 });
    } catch (e) {
      message.error(e?.error || '加载失败');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = async (id) => {
    try {
      const r = await sslService.getAcme(id);
      setDetail(r.data);
      setItemId('');
    } catch (e) { message.error(e?.error || '加载失败'); }
  };

  const refresh = async (id, item) => {
    try {
      const r = await sslService.refreshAcme(id, item);
      if (r.success === false) throw r;
      message.success('已刷新');
      load(page.current, page.pageSize);
      if (detail?.id === id) open(id);
    } catch (e) { message.error(e?.error || '刷新失败'); }
  };

  const cancel = async (id) => {
    try {
      const r = await sslService.cancelAcme(id, '管理员取消');
      if (r.success === false) throw r;
      message.success('已取消（如需退款请在账单中退款）');
      setDetail(null);
      load(page.current, page.pageSize);
    } catch (e) { message.error(e?.error || '取消失败'); }
  };

  const slotText = (s) => (s ? `${s.used}/${s.total}` : '-');
  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: '用户', dataIndex: 'user_id', width: 80 },
    { title: '主域名', dataIndex: 'domain', render: (v) => v || '-' },
    { title: '状态', dataIndex: 'status', render: (_, r) => { const [l, c] = STATUS[r.status] || [r.status, 'default']; return <Tag color={c}>{l}</Tag>; } },
    { title: '名额 普通 / 通配符', render: (_, r) => `${slotText(r.slots?.single)} · ${slotText(r.slots?.wildcard)}` },
    { title: 'CA item', dataIndex: 'item_id', render: (v) => v || '-' },
    { title: '付费到期', dataIndex: 'paid_until', render: (v) => (v ? String(v).slice(0, 10) : '-') },
    { title: 'CA 续费', dataIndex: 'ca_auto_renew', render: (v) => (v ? <Tag color="green">开</Tag> : <Tag>关</Tag>) },
    { title: '', width: 160, render: (_, r) => (
      <Space>
        <Button size="small" icon={<EyeOutlined />} onClick={() => open(r.id)}>详情</Button>
        <Button size="small" icon={<ReloadOutlined />} onClick={() => refresh(r.id)} />
      </Space>) },
  ];

  return (
    <Card title="ACME 订阅" style={{ marginTop: 16 }}
      extra={<Button icon={<ReloadOutlined />} onClick={() => load(page.current, page.pageSize)} loading={loading}>刷新</Button>}>
      <Table rowKey="id" size="small" columns={columns} dataSource={rows} loading={loading} scroll={{ x: 'max-content' }}
        pagination={{ ...page, onChange: (p, s) => load(p, s) }}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无 ACME 订阅" /> }} />
      <Modal title={detail ? `ACME 订阅 #${detail.id}` : ''} open={!!detail} onCancel={() => setDetail(null)} footer={null} width={760}>
        {detail && (
          <>
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="用户">{detail.user_id}</Descriptions.Item>
              <Descriptions.Item label="订阅">#{detail.subscription_id}（{detail.subscription_status || '-'}）</Descriptions.Item>
              <Descriptions.Item label="状态">{(STATUS[detail.status] || [detail.status])[0]}</Descriptions.Item>
              <Descriptions.Item label="CA 状态">{detail.ca_status || '-'}</Descriptions.Item>
              <Descriptions.Item label="CA 订单 / item">{detail.ca_order_id || '-'} / {detail.item_id || '-'}</Descriptions.Item>
              <Descriptions.Item label="ACME 账户">{detail.account_id || '-'}</Descriptions.Item>
              <Descriptions.Item label="EAB KID" span={2}><code>{detail.eab_kid || '-'}</code></Descriptions.Item>
              <Descriptions.Item label="服务器" span={2}><code>{detail.server_url || '-'}</code></Descriptions.Item>
              <Descriptions.Item label="CA 周期">{detail.begin || '-'} → {detail.end || '-'}</Descriptions.Item>
              <Descriptions.Item label="CA 下次续费">{detail.next_renewal || '-'}{detail.ca_auto_renew ? '' : '（已关）'}</Descriptions.Item>
              <Descriptions.Item label="付费到期">{detail.paid_until ? String(detail.paid_until).slice(0, 10) : '-'}</Descriptions.Item>
              <Descriptions.Item label="退款期至">{detail.refund_until ? String(detail.refund_until).slice(0, 10) : '-'}</Descriptions.Item>
              {detail.last_error && <Descriptions.Item label="最近错误" span={2}>{detail.last_error}</Descriptions.Item>}
              {detail.cancel && <Descriptions.Item label="取消" span={2}>{detail.cancel.at} {detail.cancel.ok ? '成功' : `失败：${detail.cancel.error}`}</Descriptions.Item>}
            </Descriptions>
            <Table style={{ marginTop: 12 }} size="small" pagination={false} rowKey={(r, i) => `${r.name}-${i}`}
              dataSource={detail.domains || []}
              columns={[
                { title: '域名', dataIndex: 'name' },
                { title: '类型', dataIndex: 'type', render: (v) => (v === 'wildcard' ? '通配符' : '普通') },
                { title: '状态', render: (_, r) => (r.status === 'removed' ? <Tag>已删除 {String(r.removed_at || '').slice(0, 10)}</Tag> : <Tag color="green">{r.ca_status || '已添加'}</Tag>) },
                { title: '添加时间', dataIndex: 'added_at', render: (v) => String(v || '').slice(0, 10) },
              ]} />
            <Space style={{ marginTop: 12 }} wrap>
              <Tooltip title="CA 创建超时时填写 GoGetSSL 后台的 item id 以接管">
                <Input placeholder="item id（可选）" value={itemId} onChange={(e) => setItemId(e.target.value.replace(/\D/g, ''))} style={{ width: 160 }} />
              </Tooltip>
              <Button icon={<ReloadOutlined />} onClick={() => refresh(detail.id, itemId ? Number(itemId) : undefined)}>从 CA 刷新</Button>
              {detail.status !== 'cancelled' && (
                <Popconfirm title="在 CA 取消并结束订阅？ACME 账户立即失效。退款请另在账单中操作。" okButtonProps={{ danger: true }} onConfirm={() => cancel(detail.id)}>
                  <Button danger>取消订阅</Button>
                </Popconfirm>
              )}
            </Space>
          </>
        )}
      </Modal>
    </Card>
  );
}
